import { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { isRefreshUrl, isTaktileRequest } from "src/api/middlewares/utils";
import { adoptSharedSession } from "src/api/tokenStorage";
import { TokenWithUserAndKey } from "src/clients/taktile-api";
import { getSignUpUrlWithNext } from "src/router/urls";
import { RefreshRequestConfig } from "src/store/api";

const NO_REFRESH_TOKEN_ERROR_MESSAGE = "No refresh token";

type RefreshError = { message?: string; request?: { status?: number } };

const isSessionOver = (error: RefreshError): boolean =>
  error.message === NO_REFRESH_TOKEN_ERROR_MESSAGE ||
  error.request?.status === 401;

type Refresh = (
  options?: RefreshRequestConfig,
) => Promise<void | TokenWithUserAndKey>;

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  authRetried?: boolean;
  recoveryRefresh?: boolean;
};

const refreshOnce = (refresh: Refresh): Promise<TokenWithUserAndKey> =>
  refresh().then((response) => {
    if (response === undefined) {
      throw new Error(NO_REFRESH_TOKEN_ERROR_MESSAGE);
    }
    return response;
  });

const refreshWithAdoptedSession = async (
  refresh: Refresh,
): Promise<TokenWithUserAndKey | null> => {
  if (!adoptSharedSession()) {
    return null;
  }
  try {
    return await refreshOnce(refresh);
  } catch (adoptedError) {
    if (!isSessionOver(adoptedError as RefreshError)) {
      throw adoptedError;
    }
    return null;
  }
};

const recoverSession = async (
  refresh: Refresh,
  resetAll: () => void,
  onSettled: () => void,
): Promise<TokenWithUserAndKey | null> => {
  // Tag the recovery's own refresh POSTs: their 401s must reach this function
  // raw, while independent refresh callers join the shared recovery below.
  const ownRefresh = (): Promise<void | TokenWithUserAndKey> =>
    refresh({ recoveryRefresh: true });
  try {
    return await refreshOnce(ownRefresh);
  } catch (refreshError) {
    if (!isSessionOver(refreshError as RefreshError)) {
      throw refreshError;
    }
    const adoptedToken = await refreshWithAdoptedSession(ownRefresh);
    if (adoptedToken !== null) {
      return adoptedToken;
    }
    const pathname = window.location.pathname;
    if (!pathname.startsWith("/sign-up") && !pathname.startsWith("/callback")) {
      window.location.href = getSignUpUrlWithNext();
      resetAll();
    }
    return null;
  } finally {
    onSettled();
  }
};

export const add401ErrorsMiddleware = (
  axiosInstance: AxiosInstance,
  refresh: Refresh,
  resetAll: () => void,
) => {
  let recoveryInFlight: Promise<TokenWithUserAndKey | null> | null = null;
  const recover = (): Promise<TokenWithUserAndKey | null> => {
    if (recoveryInFlight === null) {
      recoveryInFlight = recoverSession(refresh, resetAll, () => {
        recoveryInFlight = null;
      });
    }
    return recoveryInFlight;
  };

  const interceptor: number = axiosInstance.interceptors.response.use(
    undefined,
    (error) => {
      if (error.response?.config?.sourceScoped) {
        return Promise.reject(error);
      }

      const responseURL = error.request?.responseURL;
      if (!isTaktileRequest(responseURL) || error.request.status !== 401) {
        return Promise.reject(error);
      }

      if (isRefreshUrl(responseURL)) {
        const refreshConfig: RetriableRequestConfig | undefined =
          error.response?.config;
        if (refreshConfig?.recoveryRefresh) {
          return Promise.reject(error);
        }
        return recover().then((token) => {
          if (token === null) {
            return Promise.reject(error);
          }
          return {
            ...error.response,
            status: 200,
            statusText: "OK",
            data: token,
          };
        });
      }

      const config: RetriableRequestConfig | undefined = error.response?.config;
      if (config?.authRetried) {
        return Promise.reject(error);
      }

      return recover().then((token) => {
        if (token === null || config === undefined) {
          return Promise.reject(error);
        }
        config.headers["Authorization"] = "Bearer " + token.id_token;
        config.authRetried = true;
        return axiosInstance.request(config);
      });
    },
  );

  return interceptor;
};
